import dayjs from 'dayjs';
import { t } from 'i18n';
import _ from 'lodash';
import { storage } from 'models/store-global';
import { Variant } from 'react-bootstrap/esm/types';
import { ZodError, ZodIssue } from 'zod';

import AuthService from 'services/AuthService';
import LoginAsUserService from 'services/LoginAsUserService';
import { createId, sleep } from 'utils/common-helper';

import { MyStore } from 'types/Store';

export interface AlertInterface {
    variant: Variant;
    // TODO: Combine 'component' and 'message' to 'content'
    message?: string; // Get rid of this attribute
    component?: any; // Get rid of this attribute
    collapseAutomatically?: boolean;
    collapseDelay?: number;
    show?: boolean;
    timestamp?: number;
}

const AlertsService = {
    showError: (message: string, component?: any) => {
        AlertsService.showAlert({ variant: 'danger', message, component, collapseDelay: 15000 });
    },

    showWarning: (message: string, component?: any) => {
        AlertsService.showAlert({ variant: 'warning', message, component });
    },

    showSuccess: (message: string, component?: any) => {
        AlertsService.showAlert({ variant: 'success', message, component });
    },

    showAlert: (alert: AlertInterface) => {
        const { collapseDelay = 5000, collapseAutomatically = true } = alert;
        const id = createId();
        storage.set(
            (store): MyStore => ({
                ...store,
                alerts: {
                    ...(store.alerts || {}),
                    [id]: { ...alert, timestamp: +new Date() },
                },
            }),
        );
        if (collapseAutomatically) {
            setTimeout(() => {
                AlertsService.hideAlert(id);
            }, collapseDelay);
        }
        return id;
    },

    hideAlert: async (id: string) => {
        storage.set(
            (store): MyStore => ({
                ...store,
                alerts: {
                    ...(store.alerts || {}),
                    [id]: {
                        ...(store.alerts?.[id] || {}),
                        show: false,
                    } as AlertInterface,
                },
            }),
        );
        await sleep(200);
        storage.set((store): MyStore => {
            delete store.alerts?.[id];
            return store;
        });
    },

    hideAll: () => {
        storage.set((store): MyStore => ({ ...store, alerts: {} }));
    },

    // TODO: Handle only axios errors. Move all other errors to the showError method.
    handleError: (error: any, customHandler?: (errorObject: any) => boolean) => {
        console.trace(error);
        if (error === undefined) {
            return;
        }

        if (error instanceof ZodError) {
            return AlertsService.handleValidationError(error);
        }

        error = error?.response?.data ?? error?.message ?? error;

        if (error.then) {
            return;
        }

        if (error.errorCode === 'user_signed_in_while_working_on_behalf') {
            LoginAsUserService.signOutUserOrAdmin();
            return;
        }

        let message = '';
        let errorObject = null;
        try {
            errorObject = typeof error === `string` ? JSON.parse(error) : error;
            if (errorObject.debug) {
                console.error(errorObject.debug);
            }
            message = errorObject?.message;
        } catch (e) {
            message = error;
        }

        if (message) {
            const isUnauthorized = [
                'Unauthorized',
                'User is not authorized to access this resource with an explicit deny',
                'Advisors are no longer allowed to access this resource',
                'Request failed with status code 401',
            ].includes(message);

            if (isUnauthorized) {
                LoginAsUserService.beforeLogout();
                AuthService.logout();
                return;
            }

            if (AlertsService.isNetworkError(message)) {
                AlertsService.showError(t('errors:common.failedToFetch'));
                return;
            }
            AlertsService.showError(message);
            console.error(message);
            return;
        }

        if (errorObject && customHandler && customHandler(errorObject)) {
            return;
        }

        const hasErrorCode = !!errorObject && !!errorObject.errorCode;
        const shouldDisplayByCode = hasErrorCode && !!errorObject.user;

        if (shouldDisplayByCode) {
            const fieldValue = _.isString(errorObject.field) ? errorObject.field : JSON.stringify(errorObject.field);
            const messageToShow = t(`errors:${errorObject.errorCode}`, {
                field: fieldValue,
                returnObjects: true,
            });
            if (_.isString(messageToShow)) {
                AlertsService.showError(messageToShow);
            }
            if (_.isObject(messageToShow) && (messageToShow as any)?.message) {
                AlertsService.showAlert({ variant: 'danger', ...messageToShow });
            }
        } else {
            AlertsService.showError(t(`errors:base`, { errorId: errorObject?.errorId || dayjs().format('hhmmss') }));
            console.error(hasErrorCode ? errorObject.errorCode : error);
        }
    },

    isNetworkError: (message: any) => {
        return (
            typeof message === 'string' &&
            [
                'failed to fetch',
                'network error',
                'network error communicating with endpoint',
                'load failed',
                'timeout',
            ].includes(message.toLowerCase())
        );
    },

    handleValidationError: (error: ZodError) => {
        error.flatten((issue: ZodIssue) => {
            console.error(issue);
            AlertsService.showError(issue.message);
            return {
                message: issue.message,
                errorCode: issue.code,
            };
        });
    },
};

declare global {
    interface Window {
        alerts: any;
    }
}

window.alerts = AlertsService;

export default AlertsService;
